import { pointTime, spanText, untilTime } from '../../shared/lib/format';
import type { TCopy, TCopyStatus, TNodeRef, TRun } from './api';

export type TStatusTone = 'success' | 'failure' | 'motion' | 'suppressed';

// 00-rules: "Status is a shape and a word, never a colour alone".
export type TStatusShape =
  | 'done'
  | 'recovered'
  | 'failed'
  | 'retrying'
  | 'waiting'
  | 'paused'
  | 'stopped'
  | 'merged'
  | 'running';

export interface IStatusView {
  tone: TStatusTone;
  shape: TStatusShape;
  label: string;
  /** A second, quieter fact: "retry in 4 min". */
  note?: string;
}

export const OPEN_COPY_STATUSES: readonly TCopyStatus[] = [
  'running',
  'waiting',
  'retrying',
  'paused',
  'on_hold',
];

// The order a running run is summed up by: the copy most worth a look speaks for it.
const OPEN_PRIORITY: readonly TCopyStatus[] = [
  'retrying',
  'on_hold',
  'waiting',
  'paused',
  'running',
];

// 00-rules: Delivered is "reached a Deliver or app step and it succeeded"; anything else that
// ended cleanly is Finished.
const sends = (node: TNodeRef | null): boolean =>
  node?.type === 'deliver' || node?.type === 'action';

const STOP_REASON: Record<string, string> = {
  stopped_by_user: 'Stopped by hand',
  flow_deleted: 'Stopped · flow deleted',
  retention_expired: 'Stopped · kept past retention',
  'agent.limit_steps': 'Hit a limit · steps',
  'agent.limit_cost': 'Hit a limit · cost',
  'agent.limit_time': 'Hit a limit · time',
  'agent.limit_budget': 'Hit a limit · AI budget',
  'agent.limit_queue': 'Hit a limit · waited too long',
};

const AGENT_WAIT: Record<string, string> = {
  child_run: 'Waiting · its agent runs a flow',
  job: 'Waiting · its agent runs a server job',
  memory_queue: 'Queued · behind another run of this chat',
  ask: 'Waiting for approval',
};

export const copyStatusView = (copy: TCopy, now: number, timeZone?: string): IStatusView => {
  switch (copy.status) {
    case 'completed': {
      const word = sends(copy.node) ? 'Delivered' : 'Finished';
      // Failed here once, then a redelivery of it got through (§10.3): no error branch ran.
      if (copy.redeliveredAt && copy.recoveredAt) {
        return { tone: 'success', shape: 'recovered', label: 'Redelivered' };
      }
      return copy.recoveredAt
        ? {
            tone: 'success',
            shape: 'recovered',
            label: `${word} · recovered at ${copy.recoveredAt.name}`,
          }
        : { tone: 'success', shape: 'done', label: word };
    }
    case 'failed':
    case 'error':
      return { tone: 'failure', shape: 'failed', label: copy.statusLabel };
    case 'retrying':
      return {
        tone: 'failure',
        shape: 'retrying',
        label:
          copy.maxAttempts > 0
            ? `Retrying · ${copy.attemptCount} of ${copy.maxAttempts}`
            : copy.statusLabel,
        note: copy.nextAttemptAt
          ? `retry ${untilTime(copy.nextAttemptAt, now, timeZone)}`
          : undefined,
      };
    case 'waiting':
      if (copy.errorClass === 'ai.budget_exhausted' || copy.waitReason === 'budget') {
        return { tone: 'suppressed', shape: 'paused', label: 'Paused · AI budget' };
      }
      if (copy.waitReason && AGENT_WAIT[copy.waitReason]) {
        return { tone: 'motion', shape: 'waiting', label: AGENT_WAIT[copy.waitReason] };
      }
      return {
        tone: 'motion',
        shape: 'waiting',
        label: copy.waitingUntil
          ? `Waiting · until ${pointTime(copy.waitingUntil, now, timeZone)}`
          : copy.statusLabel,
      };
    case 'paused':
      return { tone: 'suppressed', shape: 'paused', label: 'Paused' };
    case 'on_hold':
      return { tone: 'suppressed', shape: 'paused', label: 'On hold' };
    case 'filtered':
      return { tone: 'suppressed', shape: 'stopped', label: copy.statusLabel };
    case 'duplicate':
      return {
        tone: 'suppressed',
        shape: 'stopped',
        label: copy.node ? `Stopped at ${copy.node.name} · duplicate` : 'Duplicate',
      };
    case 'stopped': {
      const reason = copy.errorClass ? STOP_REASON[copy.errorClass] : undefined;
      return {
        tone: 'suppressed',
        shape: 'stopped',
        label:
          reason && copy.node && !copy.errorClass?.startsWith('agent.')
            ? `${reason} at ${copy.node.name}`
            : (reason ?? copy.statusLabel),
      };
    }
    case 'merged':
      return { tone: 'suppressed', shape: 'merged', label: copy.statusLabel };
    default:
      return { tone: 'motion', shape: 'running', label: 'Running' };
  }
};

export const runStatusView = (run: TRun, now: number, timeZone?: string): IStatusView => {
  const shown = run.copies.filter((copy) => copy.status !== 'merged');
  switch (run.status) {
    case 'running': {
      for (const status of OPEN_PRIORITY) {
        const leading = shown.find((copy) => copy.status === status);
        if (leading && status !== 'running') return copyStatusView(leading, now, timeZone);
      }
      const elapsed = now - new Date(run.at).getTime();
      return {
        tone: 'motion',
        shape: 'running',
        label: elapsed >= 60_000 ? `Running · ${spanText(elapsed)}` : 'Running',
      };
    }
    case 'completed': {
      const done = shown.filter((copy) => copy.status === 'completed');
      const word =
        done.length > 0 && done.every((copy) => sends(copy.node)) ? 'Delivered' : 'Finished';
      const redelivered = done.filter((copy) => copy.redeliveredAt).length;
      const recovered = done.filter((copy) => copy.recoveredAt && !copy.redeliveredAt).length;
      const lead = redelivered > 0 ? 'Redelivered' : word;
      return {
        tone: 'success',
        shape: redelivered > 0 ? 'recovered' : 'done',
        label: recovered > 0 ? `${lead} · ${recovered.toLocaleString('en-US')} recovered` : lead,
      };
    }
    case 'failed':
    case 'error':
      return { tone: 'failure', shape: 'failed', label: run.statusLabel };
    case 'duplicate':
      return {
        tone: 'suppressed',
        shape: 'stopped',
        label: run.statusNode ? `Stopped at ${run.statusNode.name} · duplicate` : 'Duplicate',
      };
    case 'stopped': {
      const byHand = shown.find((copy) => copy.errorClass && STOP_REASON[copy.errorClass]);
      if (byHand?.errorClass) {
        return { tone: 'suppressed', shape: 'stopped', label: STOP_REASON[byHand.errorClass] };
      }
      return { tone: 'suppressed', shape: 'stopped', label: run.statusLabel };
    }
    default:
      return { tone: 'suppressed', shape: 'stopped', label: run.statusLabel };
  }
};

// Board 09 "Copies": what went on down the flow — a run the Filter stopped made none.
const NOT_A_COPY = new Set(['merged', 'filtered', 'duplicate']);

export const runCopyCount = (run: TRun): number =>
  run.summary.counts
    .filter((count) => !NOT_A_COPY.has(count.status))
    .reduce((sum, count) => sum + count.count, 0);

export const tookText = (run: Pick<TRun, 'at' | 'finishedAt'>): string =>
  run.finishedAt ? spanText(new Date(run.finishedAt).getTime() - new Date(run.at).getTime()) : '—';

export type TStartedBy =
  | { kind: 'request'; id: string }
  | { kind: 'schedule'; text: string }
  | { kind: 'manual'; text: string };

export const startedBy = (run: TRun, now: number, timeZone?: string): TStartedBy => {
  if (run.request) return { kind: 'request', id: run.request.id };
  if (run.triggerKind === 'schedule' && run.scheduledFor) {
    return { kind: 'schedule', text: pointTime(run.scheduledFor, now, timeZone) };
  }
  if (run.origin === 'agent') return { kind: 'manual', text: 'An agent' };
  return { kind: 'manual', text: run.triggerKind === 'schedule' ? 'Schedule' : 'By hand' };
};

const STUCK = new Set<TCopyStatus>(['failed', 'retrying']);

const plural = (count: number, word: string) =>
  `${count.toLocaleString('en-US')} ${word}${count === 1 ? '' : 's'}`;

// Where a copy went and what it met, from the facts a list row carries: the list has no path.
export const copyPathText = (copy: TCopy): string => {
  const parts: string[] = [];
  if (copy.node) parts.push(copy.node.name);
  if (copy.lane) parts.push(copy.lane);
  // The counters span the copy's whole path, so they only describe the step it is stuck on.
  if (STUCK.has(copy.status) && copy.attemptCount > 0) {
    parts.push(plural(copy.attemptCount, 'attempt'));
    if (copy.responseStatus !== null) parts.push(String(copy.responseStatus));
  }
  if (copy.reason) parts.push(copy.reason);
  if (copy.recoveredAt && copy.status !== 'completed') {
    parts.push(`error branch at ${copy.recoveredAt.name}`);
  }
  return parts.join(' · ');
};

export const copyPlace = (copy: Pick<TCopy, 'index' | 'total'>): string | null =>
  copy.total > 0 ? `${copy.index}/${copy.total}` : null;
